<template>
  <div>
    <div class="flex justify-between items-center">
      <h1 class="text-4xl font-extrabold pl-2 tracking-tight text-gray-900 my-4">
        {{ $t("Users") }}
      </h1>
      <div>
        <GenericButton @onClick="addUserModalOpen = true">
          {{ $t("Add a user") }}
        </GenericButton>
        <Modal :open="addUserModalOpen" @close="addUserModalOpen = false">
          <MerchantProfileForm @success="profileCreated" />
        </Modal>
      </div>
    </div>

    <SearchBar
      :current-page="currentPage"
      :placeholder="'Recherche par nom/email'"
      :available-filters="[]"
      :selected-filters="[]"
      @updateKeywordSearch="(keywords) => fetchProfiles({ keywords })"
    />

    <Table
      class="mt-10"
      :columns="columns"
      :data="profilesData"
      :is-updatable="true"
      @update="
        (profile) => {
          editProfile = profile;
        }
      "
      :is-deletable="true"
      @delete="deleteProfile"
    >
      <template #salesChannels="{ row }">
        {{ salesChannels(row).slice(0, 2).join(", ") }}
        <template v-if="salesChannels(row).length > 2">
          <i @click="showProfileStores = row">, +{{ salesChannels(row).length - 2 }} autres</i>
        </template>
      </template>

      <template #signedUp="{ row }">
        <FieldSpan
          :style-to-add="
            row.signedUp
              ? 'text-green-800 bg-green-200 border border-green-300'
              : 'text-red-800 bg-red-200 border border-red-300'
          "
          :value="row.signedUp ? 'Actif' : 'Inactif'"
        />
      </template>

      <template #role="{ row }">
        <FieldSpan
          :style-to-add="'bg-blue-100 text-blue-800 border border-blue-300'"
          :value="row.role"
          :translation-prefix="'roles'"
        />
      </template>
    </Table>

    <Pagination
      :currentPage="currentPage"
      :totalPages="totalPages"
      :totalCount="totalCount"
      @changePage="(page) => fetchProfiles({ page, keywords })"
    />

    <Modal :open="showProfileStores !== null" @close="showProfileStores = null">
      <ul>
        <li v-for="salesChannel in salesChannels(showProfileStores)" :key="salesChannel">
          {{ salesChannel }}
        </li>
      </ul>
    </Modal>
    <Modal :open="!!editProfile" @close="editProfile = null">
      <MerchantProfileForm v-if="editProfile" :profile="editProfile" @success="profileUpdated" />
    </Modal>
  </div>
</template>
<script>
import FieldSpan from "@/components/utils/FieldSpan.vue";
import { Modal, Pagination, Table } from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";
import SearchBar from "@/components/menu/SearchBar.vue";
import GenericButton from "@/components/utils/GenericButton.vue";
import MerchantProfileForm from "@/components/MerchantProfileForm.vue";

export default {
  components: {
    Table,
    Pagination,
    SearchBar,
    FieldSpan,
    GenericButton,
    Modal,
    MerchantProfileForm,
  },
  async mounted() {
    await this.fetchProfiles();
  },
  data() {
    return {
      showProfileStores: null,
      addUserModalOpen: false,
      editProfile: null,
    };
  },
  computed: {
    ...mapState("store", ["isLoading"]),
    ...mapState("merchantProfiles", ["index", "currentPage", "totalPages", "totalCount"]),
    ...mapState("auth", ["currentUser"]),
    ...mapGetters("auth", ["hasAccessToAdminDashboard", "isOrganizationAdmin"]),
    profilesData() {
      return this.index.map((profile) => ({
        ...profile,
        isUpdatable: this.canTouch(profile),
        isDeletable: this.canTouch(profile),
      }));
    },
    columns() {
      return [
        {
          label: "Nom",
          field: "fullName",
        },
        {
          label: "Email",
          field: "email",
        },
        {
          label: "Points de vente",
          field: "stores",
          slot: "salesChannels",
        },
        {
          label: "Statut",
          field: "signedUp",
          slot: "signedUp",
        },
        {
          label: "Role",
          field: "role",
          slot: "role",
        },
      ];
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("merchantProfiles", ["fetchIndex", "delete"]),
    async fetchProfiles({ page, keywords } = { page: 1, keywords: null }) {
      await this.fetchIndex({ page, keywords, includes: ["stores"] });
    },
    async profileCreated() {
      this.addUserModalOpen = false;
      await this.fetchProfiles();
    },
    async deleteProfile(profile) {
      if (!confirm(`Supprimer ${profile.fullName} ?`)) return;

      const success = await this.delete(profile.id);
      if (!success) return;

      await this.fetchProfiles();
      await this.notify({
        category: "simple",
        type: "success",
        title: "Utilisateur supprimé",
      });
    },
    canTouch(profile) {
      if (this.currentUser.email === profile.email) return false;
      if (!this.isOrganizationAdmin && profile.role === "organization_admin") return false;

      return this.hasAccessToAdminDashboard;
    },
    async profileUpdated() {
      this.editProfile = null;
      await this.fetchProfiles();
    },
    salesChannels(row) {
      return row.stores.map((store) => store.salesChannel).flat();
    },
  },
};
</script>
